import {
  Check,
  Eye,
  Mail,
  Settings,
  Rocket,
  Calendar,
  Target,
  Users,
  Lock,
} from 'lucide-react'
import { Link } from 'react-router-dom'

import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'

export type WizardStep =
  | 'review-leads'
  | 'sequence'
  | 'settings'
  | 'review-emails'
  | 'launch'

interface StepDefinition {
  key: WizardStep
  label: string
  icon: typeof Mail
  requiresContacts?: boolean
}

export const ALL_STEPS: StepDefinition[] = [
  { key: 'review-leads', label: 'Contacts', icon: Users },
  { key: 'sequence', label: 'Build', icon: Mail },
  { key: 'settings', label: 'Settings', icon: Settings },
  {
    key: 'review-emails',
    label: 'Review',
    icon: Eye,
    requiresContacts: true,
  },
  {
    key: 'launch',
    label: 'Launch',
    icon: Rocket,
    requiresContacts: true,
  },
]

interface SequenceWizardSidebarProps {
  currentStep: WizardStep
  completedSteps: Set<WizardStep>
  accessibleSteps?: Set<WizardStep>
  campaign: Campaign | undefined
  campaignUrl?: string
  hasContacts: boolean
  onStepClick: (step: WizardStep) => void
  visibleStepsOverride?: WizardStep[]
  title?: string
}

export function SequenceWizardSidebar({
  currentStep,
  completedSteps,
  accessibleSteps,
  campaign,
  campaignUrl,
  hasContacts,
  onStepClick,
  visibleStepsOverride,
  title = 'Sequence Wizard',
}: SequenceWizardSidebarProps) {
  const visibleSteps = visibleStepsOverride
    ? ALL_STEPS.filter((step) => visibleStepsOverride.includes(step.key))
    : hasContacts
      ? ALL_STEPS
      : ALL_STEPS.filter((s) => !s.requiresContacts)

  const lockedSteps = ALL_STEPS.filter((s) => s.requiresContacts)

  const formattedLaunchDate = campaign?.launch_date
    ? new Date(campaign.launch_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  return (
    <div className="flex w-64 shrink-0 flex-col border-r border-line bg-white">
      {/* Campaign context */}
      <div className="flex h-28 flex-col justify-center border-b border-line px-5">
        <p className="mb-1 text-xxs uppercase tracking-wider text-fg-secondary">
          {title}
        </p>
        <h3 className="truncate text-sm font-medium">
          {campaignUrl ? (
            <Link
              to={campaignUrl}
              className="text-brand hover:underline"
              title={`Back to ${campaign?.name ?? 'campaign'}`}
            >
              {campaign?.name ?? 'Untitled Campaign'}
            </Link>
          ) : (
            <span className="text-fg">
              {campaign?.name ?? 'Untitled Campaign'}
            </span>
          )}
        </h3>
        {formattedLaunchDate && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-fg-secondary">
            <Calendar className="size-3" />
            Launch: {formattedLaunchDate}
          </p>
        )}
      </div>

      {/* Step navigation */}
      <nav className="flex-1 overflow-auto px-3 py-4">
        <div className="space-y-1">
          {visibleSteps.map((step) => {
            const isCompleted = completedSteps.has(step.key)
            const isCurrent = currentStep === step.key
            const isAccessible =
              accessibleSteps?.has(step.key) ?? (isCompleted || isCurrent)
            const StepIcon = step.icon

            return (
              <button
                type="button"
                key={step.key}
                onClick={() => onStepClick(step.key)}
                disabled={!isAccessible}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-all',
                  isCurrent && 'bg-brand/10 text-brand',
                  isCompleted &&
                    !isCurrent &&
                    'cursor-pointer text-fg hover:bg-surface-hover',
                  !isAccessible && 'cursor-default text-fg-secondary/50'
                )}
              >
                <div
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors',
                    isCompleted && 'bg-brand text-white',
                    isCurrent &&
                      !isCompleted &&
                      'border-2 border-brand bg-brand/10 text-brand',
                    !isCompleted &&
                      !isCurrent &&
                      'bg-surface-alt text-fg-secondary/40'
                  )}
                >
                  {isCompleted ? (
                    <Check className="size-3.5" />
                  ) : (
                    <StepIcon className="size-3.5" />
                  )}
                </div>
                <span>{step.label}</span>
              </button>
            )
          })}

          {/* Locked steps indicator when no contacts — hidden for live campaigns */}
          {!hasContacts && lockedSteps.length > 0 && !visibleStepsOverride && (
            <div className="mt-2 border-t border-dashed border-line px-3 pt-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-fg-secondary/50">
                <Lock className="size-3" />
                <span>Requires contacts</span>
              </div>
              {lockedSteps.map((step) => {
                const StepIcon = step.icon
                return (
                  <div
                    key={step.key}
                    className="flex items-center gap-3 py-2 text-sm text-fg-secondary/30"
                  >
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-alt">
                      <StepIcon className="size-3.5" />
                    </div>
                    <span>{step.label}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </nav>

      {/* Campaign info footer */}
      <div className="flex min-h-20 flex-col justify-center border-t border-line bg-surface-sunken px-4 py-3">
        <p className="mb-1 text-xxs uppercase tracking-wider text-fg-secondary">
          Campaign Info
        </p>
        <div className="flex items-center gap-1 text-xs text-fg-secondary">
          <Target className="size-3" />
          <span>
            {(campaign?.contact_company_count ?? 0) > 0
              ? `${campaign?.contact_company_count} targets`
              : 'No targets'}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-fg-secondary">
          <Users className="size-3" />
          <span>
            {(campaign?.contact_count ?? 0) > 0
              ? `${campaign?.contact_count} contacts`
              : 'No contacts'}
          </span>
        </div>
        {!hasContacts && (campaign?.contact_count ?? 0) === 0 && (
          <p className="mt-1.5 text-xxs leading-snug text-fg-secondary/70">
            Add contacts from the campaign page to unlock all steps.
          </p>
        )}
      </div>
    </div>
  )
}
